import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Check, X, ArrowRight } from "lucide-react";
import { trackEvent } from "@/lib/audit";

const KEY = "vs:result:last_v1";
const TTL_MS = 1000 * 60 * 60 * 24; // 24h

interface ResultPayload { won: boolean; label?: string; at: number }

function read(): ResultPayload | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as ResultPayload;
    if (!p || typeof p.at !== "number") return null;
    if (Date.now() - p.at > TTL_MS) return null;
    return p;
  } catch { return null; }
}

/** PR-33 — Result Moment surface.
 * When a user's prediction has been resolved (won or lost), surface the
 * outcome with a single dominant CTA to predict again. Frontend-only —
 * Prediction Engine writes to `localStorage` (or fires
 * `vs:prediction:resolved`) and we render here. No XP / Nation Wars /
 * sponsor coupling.
 */
export function ResultMomentCard() {
  const [data, setData] = useState<ResultPayload | null>(() => read());

  useEffect(() => {
    const onResolved = (ev: Event) => {
      const detail = (ev as CustomEvent<ResultPayload>).detail;
      if (!detail) return;
      const payload: ResultPayload = { ...detail, at: Date.now() };
      try { window.localStorage.setItem(KEY, JSON.stringify(payload)); } catch { /* noop */ }
      setData(payload);
    };
    window.addEventListener("vs:prediction:resolved", onResolved as EventListener);
    return () => window.removeEventListener("vs:prediction:resolved", onResolved as EventListener);
  }, []);

  useEffect(() => {
    if (data) void trackEvent("result_moment_view", { won: data.won });
  }, [data]);

  if (!data) return null;

  const Icon = data.won ? Check : X;
  const headline = data.won ? "✓ Acertaste" : "✗ Fallaste";
  const accent = data.won ? "border-primary/40 bg-primary/10 text-primary" : "border-destructive/40 bg-destructive/10 text-destructive";

  return (
    <section
      aria-label="Resultado de tu predicción"
      className={`overflow-hidden rounded-2xl border p-3 ${accent}`}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-background text-foreground">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-black uppercase tracking-widest">
            {headline}
          </div>
          <p className="text-[12px] leading-snug text-foreground">
            {data.label ?? "Tu predicción ya tiene resultado."} · Nueva predicción disponible.
          </p>
        </div>
        <Link
          to="/quinielas"
          onClick={() => {
            void trackEvent("result_moment_click", { won: data.won });
            try { window.localStorage.removeItem(KEY); } catch { /* noop */ }
            setData(null);
          }}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-foreground px-3 py-1.5 text-[11px] font-black text-background"
        >
          Predecir otra vez <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </section>
  );
}
